import { type MonetaryValue } from 'ezmoney';
import { useSelector } from 'react-redux';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useWalletTransactionForecastQuery } from 'modules/company/hooks';
import { type AuthType } from 'modules/invoices/transfer';
import { getShowWarningWhenNotEnoughFundsForWireTransfer } from 'modules/reimbursements/redux/selectors';
import {
  type ExpenseBill,
  type InvoiceBill,
} from 'src/core/modules/invoices/models';

import WireTransferApproverModal from './WireTransferApproverModal';

export type Props = {
  amount: MonetaryValue;
  isApproving: boolean;
  isOpen: boolean;
  isLoading?: boolean;
  approvalAuthType?: AuthType | null;
  selectedBatchId?: string;
  selectedPayments: Array<{
    id: string;
    bill: InvoiceBill | ExpenseBill | undefined;
    amount: MonetaryValue;
  }>;
  billType: 'invoice' | 'expense';
  onClose: () => void;
  onApprove: (selectedFactor?: 'totp' | 'mfa') => Promise<void>;
  onComplete: () => void;
  alternateOperationId?: string;
};

export const WireTransferApproverModalContainer = ({
  amount,
  isApproving,
  isOpen,
  isLoading = false,
  approvalAuthType,
  billType,
  selectedBatchId,
  selectedPayments,
  onApprove,
  onClose,
  onComplete,
  alternateOperationId,
}: Props) => {
  const showWarningWhenNotEnoughFundsForWireTransfer = useSelector(
    getShowWarningWhenNotEnoughFundsForWireTransfer,
  );
  const walletTransactionForecastQuery =
    useWalletTransactionForecastQuery()(amount);

  return (
    <QuerySuspense
      queryState={walletTransactionForecastQuery}
      loading={null}
      fallback={() => (
        // In case of error in the query, we fallback to the 'normal' flow (without not enough funds warning)
        // Since it shouldn't be blocking for the wire transfer flow
        <WireTransferApproverModal
          amount={amount}
          selectedPayments={selectedPayments}
          isApproving={isApproving}
          showNotEnoughFundsWarning={false}
          approvalAuthType={approvalAuthType}
          isOpen={isOpen}
          onClose={onClose}
          onApprove={onApprove}
          onComplete={onComplete}
          billType={billType}
          selectedBatchId={selectedBatchId}
        />
      )}
    >
      {({ hasEnoughFunds }) => {
        const showNotEnoughFundsWarning =
          showWarningWhenNotEnoughFundsForWireTransfer && !hasEnoughFunds;

        return (
          <WireTransferApproverModal
            amount={amount}
            selectedPayments={selectedPayments}
            isApproving={isApproving}
            showNotEnoughFundsWarning={showNotEnoughFundsWarning}
            approvalAuthType={approvalAuthType}
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={onClose}
            onApprove={onApprove}
            onComplete={onComplete}
            billType={billType}
            selectedBatchId={selectedBatchId}
            alternateOperationId={alternateOperationId}
          />
        );
      }}
    </QuerySuspense>
  );
};
